<template>
  <div
    class="tw-relative tw-flex tw-w-full tw-flex-col tw-gap-2 md:tw-w-[552px]"
  >
    <label
      :id="labelId"
      :for="inputId"
      class="tw-font-inter tw-self-stretch tw-text-base tw-font-normal tw-leading-[150%] tw-text-[#3B4143]"
    >
      {{ label }}
    </label>

    <div ref="inputWrapper" class="tw-relative tw-w-full">
      <input
        :id="inputId"
        ref="locationInput"
        v-model="searchQuery"
        :name="inputId"
        :placeholder="computedPlaceholder"
        class="tw-font-inter tw-flex tw-h-[50px] tw-w-full tw-items-center tw-gap-[10px] tw-self-stretch tw-rounded-lg tw-border tw-border-[#D1D5DB] tw-bg-white tw-px-4 tw-py-2 tw-pr-[56px] tw-text-base tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2"
        type="text"
        role="combobox"
        aria-autocomplete="list"
        :aria-owns="listId"
        :aria-expanded="showLocationDropdown"
        aria-haspopup="listbox"
        :aria-activedescendant="highlightedOptionId"
        @input="handleLocationInput"
        @focus="handleLocationFocus"
        @blur="handleLocationBlur"
        @keydown.down="(event) => handleLocationKeyNavigation('down', event)"
        @keydown.up="(event) => handleLocationKeyNavigation('up', event)"
        @keydown.enter="selectHighlightedLocation"
        @keydown.esc="closeLocationDropdown"
      />

      <button
        v-if="searchQuery"
        class="tw-absolute tw-right-[16px] tw-top-1/2 -tw-translate-y-1/2 tw-transform tw-text-gray-400 hover:tw-text-gray-600"
        type="button"
        @mousedown.prevent
        @click="clearLocationSearch"
      >
        <span class="tw-sr-only">Clear search</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="tw-h-5 tw-w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clip-rule="evenodd"
          />
        </svg>
      </button>
    </div>

    <MountingPortal mount-to="#PortalMountTo" append>
      <div
        v-if="showLocationDropdown"
        :id="listId"
        ref="locationDropdown"
        class="tw-fixed tw-z-[10020] tw-overflow-y-auto tw-overscroll-contain tw-rounded-[16px] tw-border tw-border-[#E5E7EB] tw-bg-white tw-shadow-[0_12px_30px_rgba(0,0,0,0.16)]"
        :style="dropdownStyles"
        role="listbox"
        :aria-labelledby="labelId"
      >
        <div v-if="filteredLocations.length > 0" class="tw-relative">
          <template v-if="!hasQuery">
            <h3
              class="tw-sticky tw-top-0 tw-z-[1] tw-m-0 tw-w-full tw-bg-white tw-px-[16px] tw-py-[12px] tw-pb-[6px] tw-text-[16px] tw-font-[800]"
            >
              All cities
            </h3>

            <button
              v-for="(location, index) in sortedFilteredCities"
              :id="optionId(index)"
              :key="location.key"
              type="button"
              role="option"
              tabindex="0"
              :aria-selected="highlightedLocationIndex === index"
              class="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-px-[16px] tw-py-[10px] tw-text-left hover:tw-bg-gray-100"
              :class="[
                highlightedLocationIndex === index ? 'tw-bg-gray-100' : '',
                isKeyboardNavigation ? 'keyboard-navigating' : '',
              ]"
              @click="selectLocation(location)"
              @mousedown="handleMouseDown"
              @mouseover="handleMouseOver(index)"
            >
              <div class="tw-flex tw-items-center tw-gap-[10px]">
                <span
                  class="tw-flex tw-h-[18px] tw-w-[18px] tw-items-center tw-justify-center tw-text-[#5C6A75]"
                  aria-hidden="true"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M12 21C16.5 16.8 19 13.3 19 9.75C19 5.47 15.87 2 12 2C8.13 2 5 5.47 5 9.75C5 13.3 7.5 16.8 12 21Z"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M12 12.5C13.5188 12.5 14.75 11.2688 14.75 9.75C14.75 8.23122 13.5188 7 12 7C10.4812 7 9.25 8.23122 9.25 9.75C9.25 11.2688 10.4812 12.5 12 12.5Z"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
                <span class="tw-text-[14px] tw-font-[400]">{{
                  location.name
                }}</span>
              </div>
            </button>

            <h3
              class="tw-sticky tw-top-0 tw-z-[1] tw-m-0 tw-w-full tw-bg-white tw-px-[16px] tw-py-[12px] tw-pb-[6px] tw-text-[16px] tw-font-[800]"
            >
              All universities
            </h3>

            <button
              v-for="(location, index) in sortedFilteredUniversities"
              :id="optionId(index + sortedFilteredCities.length)"
              :key="location.key"
              type="button"
              role="option"
              tabindex="0"
              :aria-selected="
                highlightedLocationIndex === index + sortedFilteredCities.length
              "
              class="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-px-[16px] tw-py-[10px] tw-text-left hover:tw-bg-gray-100"
              :class="[
                highlightedLocationIndex === index + sortedFilteredCities.length
                  ? 'tw-bg-gray-100'
                  : '',
                isKeyboardNavigation ? 'keyboard-navigating' : '',
              ]"
              @click="selectLocation(location)"
              @mousedown="handleMouseDown"
              @mouseover="handleMouseOver(index + sortedFilteredCities.length)"
            >
              <div class="tw-flex tw-items-center tw-gap-[10px]">
                <span
                  class="tw-flex tw-h-[18px] tw-w-[18px] tw-items-center tw-justify-center tw-text-[#5C6A75]"
                  aria-hidden="true"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M4 6.5L12 3L20 6.5L12 10L4 6.5Z"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M7 8.5V13.5C7 14.4 9.2 16 12 16C14.8 16 17 14.4 17 13.5V8.5"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M20 6.5V12"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M20 12C20.5523 12 21 12.4477 21 13V14.25C21 14.8023 20.5523 15.25 20 15.25C19.4477 15.25 19 14.8023 19 14.25V13C19 12.4477 19.4477 12 20 12Z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
                <span class="tw-text-[14px] tw-font-[400]">{{
                  location.name
                }}</span>
              </div>
            </button>
          </template>

          <template v-else>
            <button
              v-for="(location, index) in filteredLocations"
              :id="optionId(index)"
              :key="location.key"
              type="button"
              role="option"
              tabindex="0"
              :aria-selected="highlightedLocationIndex === index"
              class="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-px-[16px] tw-py-[10px] tw-text-left hover:tw-bg-gray-100"
              :class="[
                highlightedLocationIndex === index ? 'tw-bg-gray-100' : '',
                isKeyboardNavigation ? 'keyboard-navigating' : '',
              ]"
              @click="selectLocation(location)"
              @mousedown="handleMouseDown"
              @mouseover="handleMouseOver(index)"
            >
              <div class="tw-flex tw-items-center tw-gap-[10px]">
                <span
                  class="tw-flex tw-h-[18px] tw-w-[18px] tw-items-center tw-justify-center tw-text-[#5C6A75]"
                  aria-hidden="true"
                >
                  <svg
                    v-if="location.type === 'city'"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M12 21C16.5 16.8 19 13.3 19 9.75C19 5.47 15.87 2 12 2C8.13 2 5 5.47 5 9.75C5 13.3 7.5 16.8 12 21Z"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M12 12.5C13.5188 12.5 14.75 11.2688 14.75 9.75C14.75 8.23122 13.5188 7 12 7C10.4812 7 9.25 8.23122 9.25 9.75C9.25 11.2688 10.4812 12.5 12 12.5Z"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <svg
                    v-else
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M4 6.5L12 3L20 6.5L12 10L4 6.5Z"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M7 8.5V13.5C7 14.4 9.2 16 12 16C14.8 16 17 14.4 17 13.5V8.5"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M20 6.5V12"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M20 12C20.5523 12 21 12.4477 21 13V14.25C21 14.8023 20.5523 15.25 20 15.25C19.4477 15.25 19 14.8023 19 14.25V13C19 12.4477 19.4477 12 20 12Z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
                <span class="tw-text-[14px] tw-font-[400]">
                  <span
                    v-for="(part, partIndex) in getHighlightedParts(
                      location.name,
                      searchQuery
                    )"
                    :key="`${location.key}-part-${partIndex}`"
                    :class="part.highlight ? 'tw-font-bold' : ''"
                  >
                    {{ part.text }}
                  </span>
                </span>
              </div>

              <span class="tw-text-xs tw-capitalize tw-text-gray-500">{{
                location.type
              }}</span>
            </button>
          </template>
        </div>

        <div v-else class="tw-px-4 tw-py-3 tw-text-[14px] tw-text-gray-500">
          <template v-if="searchQuery && searchQuery.length < 2">
            Please enter at least 2 characters to search
          </template>
          <template v-else>
            No locations found matching "{{ searchQuery }}"
          </template>
        </div>
      </div>
    </MountingPortal>
  </div>
</template>

<script>
import Fuse from 'fuse.js';

export default {
  name: 'HousemateGroupLocationDropdownField',
  props: {
    modelValue: {
      type: Object,
      default: null,
    },
    cities: {
      type: Array,
      default: () => [],
    },
    universities: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      default: 'Where are you searching? (optional)',
    },
    placeholder: {
      type: String,
      default: 'Select city or university from list',
    },
  },
  data() {
    return {
      baseId: `housemate-group-location-${Math.random()
        .toString(36)
        .slice(2, 9)}`,
      searchQuery: '',
      showLocationDropdown: false,
      isLocationInputFocused: false,
      isKeyboardNavigation: false,
      highlightedLocationIndex: -1,
      internalSelectedLocation: null,
      lastInteractionWasKeyboard: false,
      blurTimeout: null,
      fuse: null,
      dropdownStyles: {},
    };
  },
  computed: {
    inputId() {
      return `${this.baseId}-input`;
    },
    labelId() {
      return `${this.baseId}-label`;
    },
    listId() {
      return `${this.baseId}-listbox`;
    },
    highlightedOptionId() {
      return this.highlightedLocationIndex >= 0
        ? this.optionId(this.highlightedLocationIndex)
        : null;
    },
    computedPlaceholder() {
      return this.isLocationInputFocused ? 'Start typing' : this.placeholder;
    },
    hasQuery() {
      return this.searchQuery.trim().length >= 2;
    },
    normalizedCities() {
      return (Array.isArray(this.cities) ? this.cities : [])
        .map((city) => {
          const cityId = Number.parseInt(city?.city_id, 10);

          if (!Number.isInteger(cityId) || cityId <= 0 || !city?.name) {
            return null;
          }

          return {
            key: `city-${city.id ?? city.slug ?? cityId}`,
            name: city.name,
            slug: city.id ?? city.slug ?? null,
            type: 'city',
            cityId,
            universityId: null,
          };
        })
        .filter(Boolean)
        .sort((left, right) => left.name.localeCompare(right.name));
    },
    normalizedUniversities() {
      return (Array.isArray(this.universities) ? this.universities : [])
        .map((university) => {
          const cityId = Number.parseInt(university?.city_id, 10);
          const universityId = Number.parseInt(university?.id, 10);

          if (
            !Number.isInteger(cityId) ||
            cityId <= 0 ||
            !Number.isInteger(universityId) ||
            universityId <= 0 ||
            !university?.name
          ) {
            return null;
          }

          return {
            key: `university-${university.slug ?? universityId}`,
            name: university.name,
            slug: university.slug ?? null,
            type: 'university',
            cityId,
            universityId,
          };
        })
        .filter(Boolean)
        .sort((left, right) => left.name.localeCompare(right.name));
    },
    allLocations() {
      return [...this.normalizedCities, ...this.normalizedUniversities];
    },
    filteredLocations() {
      if (!this.hasQuery) {
        return this.allLocations;
      }

      if (!this.fuse) {
        return [];
      }

      return this.fuse
        .search(this.searchQuery.trim(), { limit: 20 })
        .map((result) => result.item);
    },
    sortedFilteredCities() {
      return this.filteredLocations.filter(
        (location) => location.type === 'city'
      );
    },
    sortedFilteredUniversities() {
      return this.filteredLocations.filter(
        (location) => location.type === 'university'
      );
    },
    navigableLocations() {
      if (this.hasQuery) {
        return this.filteredLocations;
      }

      return [...this.sortedFilteredCities, ...this.sortedFilteredUniversities];
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(value) {
        this.internalSelectedLocation =
          value && typeof value === 'object' ? value : null;
        this.searchQuery = this.internalSelectedLocation?.name ?? '';
      },
    },
    allLocations: {
      immediate: true,
      handler(locations) {
        this.fuse = new Fuse(locations, {
          keys: ['name', 'slug', 'type'],
          threshold: 0.3,
          ignoreLocation: true,
          minMatchCharLength: 2,
        });
      },
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handleGlobalKeyInteraction, true);
    document.addEventListener(
      'mousedown',
      this.handleOutsideLocationClick,
      true
    );
    window.addEventListener('resize', this.updateDropdownPosition);
    window.addEventListener('scroll', this.updateDropdownPosition, true);
  },
  beforeUnmount() {
    document.removeEventListener(
      'keydown',
      this.handleGlobalKeyInteraction,
      true
    );
    document.removeEventListener(
      'mousedown',
      this.handleOutsideLocationClick,
      true
    );
    window.removeEventListener('resize', this.updateDropdownPosition);
    window.removeEventListener('scroll', this.updateDropdownPosition, true);

    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout);
      this.blurTimeout = null;
    }
  },
  methods: {
    optionId(index) {
      return `${this.baseId}-option-${index}`;
    },
    emitSelection(location) {
      this.internalSelectedLocation = location;
      this.$emit('update:modelValue', location);
    },
    handleLocationInput() {
      if (
        this.internalSelectedLocation &&
        this.searchQuery !== this.internalSelectedLocation.name
      ) {
        this.emitSelection(null);
      }

      this.showLocationDropdown = true;
      this.isKeyboardNavigation = false;
      this.highlightedLocationIndex = this.hasQuery ? 0 : -1;
      this.$nextTick(this.updateDropdownPosition);
    },
    selectLocation(location) {
      this.searchQuery = location.name;
      this.showLocationDropdown = false;
      this.isLocationInputFocused = false;
      this.isKeyboardNavigation = false;
      this.highlightedLocationIndex = -1;
      this.dropdownStyles = {};
      this.emitSelection(location);
    },
    clearLocationSearch(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      this.searchQuery = '';
      this.emitSelection(null);
      this.showLocationDropdown = true;
      this.isLocationInputFocused = true;
      this.highlightedLocationIndex = -1;

      this.$nextTick(() => {
        this.updateDropdownPosition();
        this.$refs.locationInput?.focus();
      });
    },
    handleLocationFocus() {
      this.showLocationDropdown = true;
      this.isLocationInputFocused = true;
      this.$nextTick(this.updateDropdownPosition);

      if (
        this.lastInteractionWasKeyboard &&
        this.navigableLocations.length > 0
      ) {
        this.isKeyboardNavigation = true;
        this.highlightedLocationIndex = 0;
        return;
      }

      this.isKeyboardNavigation = false;
      this.highlightedLocationIndex = this.hasQuery ? 0 : -1;
    },
    handleLocationBlur() {
      if (this.blurTimeout) {
        clearTimeout(this.blurTimeout);
      }

      this.blurTimeout = setTimeout(() => {
        const activeElement = document.activeElement;
        const isInDropdown =
          this.$refs.locationDropdown?.contains(activeElement);

        if (!isInDropdown && activeElement !== this.$refs.locationInput) {
          this.closeLocationDropdown();
        }
      }, 200);
    },
    closeLocationDropdown() {
      this.showLocationDropdown = false;
      this.isLocationInputFocused = false;
      this.isKeyboardNavigation = false;
      this.highlightedLocationIndex = -1;
      this.dropdownStyles = {};
    },
    handleOutsideLocationClick(event) {
      if (
        !this.$el?.contains(event.target) &&
        !this.$refs.locationDropdown?.contains(event.target)
      ) {
        this.closeLocationDropdown();
      }
    },
    handleGlobalKeyInteraction(event) {
      if (['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(event.key)) {
        this.lastInteractionWasKeyboard = true;
      }
    },
    handleMouseDown() {
      this.isKeyboardNavigation = false;
      this.lastInteractionWasKeyboard = false;
    },
    handleMouseOver(index) {
      if (!this.isKeyboardNavigation) {
        this.highlightedLocationIndex = index;
      }
    },
    handleLocationKeyNavigation(direction, event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      if (!this.showLocationDropdown) {
        this.showLocationDropdown = true;
        this.$nextTick(this.updateDropdownPosition);
      }

      const items = this.navigableLocations;

      if (!items.length) {
        return;
      }

      this.isKeyboardNavigation = true;

      if (this.highlightedLocationIndex === -1) {
        this.highlightedLocationIndex =
          direction === 'up' ? items.length - 1 : 0;
        this.scrollToHighlightedItem();
        return;
      }

      const delta = direction === 'up' ? -1 : 1;
      this.highlightedLocationIndex =
        (this.highlightedLocationIndex + delta + items.length) % items.length;
      this.scrollToHighlightedItem();
    },
    scrollToHighlightedItem() {
      this.$nextTick(() => {
        const dropdown = this.$refs.locationDropdown;
        const option = dropdown?.querySelector(`#${this.highlightedOptionId}`);

        option?.scrollIntoView({ block: 'nearest' });
      });
    },
    selectHighlightedLocation(event) {
      if (event) {
        event.preventDefault();
      }

      if (!this.showLocationDropdown) {
        return;
      }

      const location =
        this.navigableLocations[this.highlightedLocationIndex] ??
        this.navigableLocations[0] ??
        null;

      if (!location) {
        return;
      }

      this.selectLocation(location);

      this.$nextTick(() => {
        this.$refs.locationInput?.focus();
      });
    },
    updateDropdownPosition() {
      if (!this.showLocationDropdown) {
        return;
      }

      const inputWrapper = this.$refs.inputWrapper;

      if (!(inputWrapper instanceof HTMLElement)) {
        return;
      }

      const rect = inputWrapper.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - 16;
      const spaceAbove = rect.top - 16;
      const openUpwards = spaceBelow < 220 && spaceAbove > spaceBelow;
      const maxHeight = Math.max(
        160,
        Math.min(320, openUpwards ? spaceAbove : spaceBelow)
      );

      this.dropdownStyles = {
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        maxHeight: `${maxHeight}px`,
        top: openUpwards ? 'auto' : `${rect.bottom + 6}px`,
        bottom: openUpwards ? `${window.innerHeight - rect.top + 6}px` : 'auto',
      };
    },
    getHighlightedParts(text, query) {
      if (!text || typeof text !== 'string') {
        return [{ text: '', highlight: false }];
      }

      if (!query || typeof query !== 'string' || query.trim().length < 2) {
        return [{ text, highlight: false }];
      }

      const lowerText = text.toLowerCase();
      const lowerQuery = query.toLowerCase().trim();
      const matchIndex = lowerText.indexOf(lowerQuery);

      if (matchIndex === -1) {
        return [{ text, highlight: false }];
      }

      return [
        { text: text.slice(0, matchIndex), highlight: false },
        {
          text: text.slice(matchIndex, matchIndex + lowerQuery.length),
          highlight: true,
        },
        { text: text.slice(matchIndex + lowerQuery.length), highlight: false },
      ].filter((part) => part.text.length > 0);
    },
  },
};
</script>
